import { type TraverseSelector } from '@source-digital/traverse-selector'
import { resolveElementWithTraverse } from './dom'

/**
 * An interface for specifying how to insert an element into the DOM
 */
export interface ElementInsertOptions {
  /**
   * Specify the element if you want to inject into a specific element
   * Otherwise embedder will create a new element
   */
  el?: string | HTMLElement | TraverseSelector
  /**
   * Specify how to insert the element.
   * If `replace` is specified, `el` must be specified.
   * @default insertAfter
   */
  insertionMode?: 'replace' | 'appendChild' | 'insertBefore' | 'insertAfter'
}

export function createAndInsertElement (
  originEl: HTMLElement,
  options: ElementInsertOptions = {},
  createElement: () => HTMLElement = () => document.createElement('div')
): HTMLElement {
  const insertToFromOptions = options.el != null ? resolveElementWithTraverse(options.el, originEl) : null
  if (options.insertionMode === 'replace') {
    if (insertToFromOptions == null) throw new Error('Cannot use `replace` mode without specifying `el`')
    return insertToFromOptions
  }
  const newElement = createElement()
  const insertTo = insertToFromOptions ?? originEl
  insertToDom(newElement, insertTo, options.insertionMode ?? 'insertAfter')
  return newElement
}

export function insertToDom (elToInsert: Element, targetEl: Element, insertionMode: 'appendChild' | 'insertBefore' | 'insertAfter'): void {
  if (insertionMode === 'appendChild') {
    targetEl.appendChild(elToInsert)
  } else if (insertionMode === 'insertBefore') {
    targetEl.before(elToInsert)
  } else if (insertionMode === 'insertAfter') {
    targetEl.after(elToInsert)
  } else {
    throw new Error(`Unknown insertion mode: ${insertionMode as string}`)
  }
}
